import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthContext from "../../../../auth/AuthContext";
import AlertService from "../../../../services/AlertService";
import ReviewQuizMCQExamService from "../../../../services/ReviewQuizMCQExamService";
import ChronometerIcon from "../../../../assets/icons/ChronoMeter.svg";
import QuestionMarkIcon from "../../../../assets/icons/QuestionMark.svg";
import PreviousIcon from "../../../../assets/icons/Previous.svg";
import WhitePreviousIcon from "../../../../assets/icons/WhitePreviousIcon.svg";
import NextIcon from "../../../../assets/icons/Next.svg";
import WhiteNextIcon from "../../../../assets/icons/WhiteNextIcon.svg";
import { ProgressBar } from "react-bootstrap";
import ReviewMCQExamQuestion from "./ReviewMCQExamQuestion";
import { Skeleton } from "primereact/skeleton";
import { ThemeContext } from "../../../../theme/ThemeContext";
import { FaCheck, FaTimes } from "react-icons/fa";

const ReviewQuizExam = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [loading, setLoading] = useState(false);
  const [quizExam, setQuizExam] = useState();
  const [questions, setQuestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState();
  const [selectedCardId, setSelectedCardId] = useState(0);
  const [selectedCard, setSelectedCard] = useState({});
  const [timerText, setTimerText] = useState("00:00:00");
  const [answeredCount, setAnsweredCount] = useState(0);
  const [answeredCountPercent, setAnsweredCountPercent] = useState(0);
  const authContext = useContext(AuthContext);
  const scrollRef = useRef();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";

  const minutesToTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  };

  const scrollToItem = () => {
    if (scrollRef.current) {
      const parentContainer = scrollRef.current.parentElement;
      const itemRect = scrollRef.current.getBoundingClientRect();
      const parentRect = parentContainer.getBoundingClientRect();

      const isVisible =
        itemRect.top >= parentRect.top &&
        itemRect.bottom <= parentRect.bottom &&
        itemRect.left >= parentRect.left &&
        itemRect.right <= parentRect.right;

      if (!isVisible) {
        scrollRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "nearest",
        });
      }
    }
  };

  const quizAccess = authContext.getPermissions("quiz");
  if (quizAccess.filter((f) => f.access.includes(1)).length === 0) {
    navigate("/");
  }

const loadQuestions = useCallback(async (signal) => {
    setLoading(true);
    try {
      const data = await ReviewQuizMCQExamService.Get(
        state?.id,
        authContext,
        signal 
      );

      if (signal?.aborted) return; 

      if (data.isSuccess) {
        setQuizExam(data.data);
        setQuestions(data.data.questions);
        if (data.data.questions.length > 0) {
          setSelectedCardId(data.data.questions[0].id);
        }
        setSelectedIndex(0);
        setAnsweredCount(
          data.data.questions.filter((f) => f.isCorrectAnswer === true).length
        );
        setAnsweredCountPercent(
          (data.data.questions.filter((f) => f.isCorrectAnswer === true).length /
            data.data.totalMCQQuestions) * 100
        );
        setTimerText(`${minutesToTime(data.data.quizTimeInMinutes)}:00`);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return; 
    } finally {
      if (!signal?.aborted) setLoading(false); 
    }
  }, [authContext, state?.id]);

useEffect(() => {
    const controller = new AbortController(); 
    if (state?.id) {
      loadQuestions(controller.signal);
    }
    return () => controller.abort(); 
  }, [loadQuestions, state?.id]);

  const onCardClick = (data, i) => {
    let preId = selectedCardId;
    setSelectedCardId(data.id);
    setSelectedIndex(i);
    if (preId === undefined || preId === null || preId === 0) {
      setTimeout(() => {
        scrollToItem();
      }, 10);
    }
  };

  const moveToPrevious = (e) => {
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const moveToNext = (e) => {
    setSelectedIndex((prev) =>
      prev === questions.length - 1 ? prev : prev + 1
    );
  };

  useEffect(() => {
    if (questions && questions.length > 0) {
      setSelectedCardId(questions[selectedIndex].id);
      setSelectedCard(questions[selectedIndex]);
      setTimeout(() => {
        scrollToItem();
      }, 10);
    }
  }, [selectedIndex, questions]);

  return (
    <Fragment>
      <div className="container-fluid question-container mt-3">
        <div className="row d-none d-md-flex">
          <div
            className="col-md-2 col-sm-4 scroll-no-buttons"
            style={{ maxHeight: "82vh", overflowY: "auto" }}
          >
            {loading
              ? Array.from({ length: 20 }).map((_, idx) => (
                  <div key={idx}>
                    <div className="mt-1 text-center">
                      <div className="pt-1 pb-1" title="Loading">
                        <Skeleton width="100%" height="2rem" />
                      </div>
                    </div>
                  </div>
                ))
              : questions &&
                questions.length > 0 &&
                questions.map((q, i) => (
                  <div key={i} ref={q.id === selectedCardId ? scrollRef : null}>
                    <div
                      className={`
                                                mt-1 text-center cursor-pointer
                                                ${
                                                  q.id === (selectedCardId ?? 0)
                                                    ? "quiz-active-card"
                                                    : "quiz-card"
                                                }
                                                ${
                                                  q.isCorrectAnswer
                                                    ? "list-correct-answer"
                                                    : "list-wrong-answer"
                                                }
                                            `}
                      onClick={(e) => {
                        e.preventDefault();
                        onCardClick(q, i);
                      }}
                    >
                      <div
                        className={`pt-1 pb-1 cursor-pointer ${
                          q.isCorrectAnswer
                            ? "list-correct-answer"
                            : "list-wrong-answer"
                        }`}
                        title={q.question}
                      >
                        {q.isCorrectAnswer && (
                          <FaCheck className="me-2 mb-1 inline-block" />
                        )}
                        {q.isCorrectAnswer === false && (
                          <FaTimes className="me-2 mb-1 inline-block" />
                        )}
                        Question {i + 1}
                      </div>
                    </div>
                  </div>
                ))}
          </div>
          <div className="col-md-10 col-sm-8 view">
            {loading ? (
              <div className="d-flex bd-highlight">
                <div className="text-primary mt-2">
                  <Skeleton size="5rem" shape="circle" />
                </div>
                <div
                  className="p-2 mt-auto mb-auto"
                  style={{ color: "#104E66", width: "30%" }}
                >
                  <div className="title">
                    <Skeleton width="80%" height="1.5rem" />
                  </div>
                  <div className="title pt-1">
                    <Skeleton width="85%" height="1.5rem" />
                  </div>
                  <div className="title pt-1">
                    <Skeleton width="60%" height="1.5rem" />
                  </div>
                </div>

                <div className="ms-auto" style={{ width: "40%" }}>
                  <div
                    className="d-flex flex-column align-items-end"
                    style={{ width: "100%" }}
                  >
                    <div className="fw-light pt-1" title="Questions">
                      <Skeleton width="10rem" height="1.5rem" />
                    </div>
                    <div className="fw-light pt-1" title="Duration">
                      <Skeleton width="12rem" height="1.5rem" />
                    </div>
                    <div className="fw-light pt-1">
                      <Skeleton width="30rem" height="1.5rem" />
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="d-flex  bd-highlight flex-wrap flex-md-nowrap align-items-start mb-3 gap-3 quiz-header-responsive">
                <div className="text-success">
                  <svg width="120" height="120" viewBox="0 0 120 120">
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="#FF9900"
                      strokeWidth="4"
                      strokeLinecap="round"
                      className="progress-circle"
                    />
                    <text
                      x="60"
                      y="67"
                      textAnchor="middle"
                      fontSize="18"
                      fontWeight="bold"
                      fill="#FF9900"
                    >
                      {timerText}
                    </text>
                  </svg>
                </div>
                <div
                  className="p-2 mt-auto mb-auto"
                  style={{ color: isDark ? "#ffffff" : "#104E66" }}
                >
                  <div className="title">
                    Quiz : {quizExam && quizExam.quizName}
                  </div>
                  <div className="title pt-2">
                    Course : {quizExam && quizExam.courseName}
                  </div>
                  <div className="title pt-2">Quiz Type : MCQ</div>
                </div>

                <div className="ms-auto">
                  <div className="d-flex flex-column align-items-end">
                    <div className="fw-light pt-2" title="Questions">
                      <img
                        src={QuestionMarkIcon}
                        alt="Questions"
                        className="ms-2 me-1 mb-1"
                      />{" "}
                      Questions : {quizExam && quizExam.totalMCQQuestions}
                    </div>
                    <div className="fw-light pt-2" title="Duration">
                      <img
                        src={ChronometerIcon}
                        alt="Duration"
                        className=" ms-2 me-1 mb-1"
                      />{" "}
                      Duration :{" "}
                      {minutesToTime(quizExam && quizExam.quizTimeInMinutes)}
                    </div>
                    <div className="fw-light pt-2 w-100">
                      <div className="d-flex" style={{ width: "100%" }}>
                        <div>Completed </div>
                        <div
                          className="ms-2 me-2 flex-grow-1"
                          style={{ minWidth: 301 }}
                        >
                          <ProgressBar
                            className="mt-2"
                            style={{ height: "10px" }}
                            variant={
                              answeredCountPercent < 25
                                ? "danger"
                                : answeredCountPercent < 50
                                ? "warning"
                                : answeredCountPercent < 75
                                ? "info"
                                : "success"
                            }
                            now={answeredCountPercent}
                          />
                        </div>
                        <div>
                          {answeredCount} /{" "}
                          {quizExam && quizExam.totalMCQQuestions}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {loading ? (
              <div>
                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                  <Skeleton width="100%" height="2px" />
                </div>
                <div className="d-flex ps-3 pe-3 mb-3">
                  <Skeleton width="60%" height="2rem" />
                </div>
                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                  <Skeleton width="100%" height="2px" />
                </div>

                <div className="d-flex ps-3 pe-3 mb-3">
                  <center style={{ width: "5%" }} className="pe-1">
                    <Skeleton width="2rem" height="2rem" shape="circle" />
                  </center>
                  <div style={{ width: "100%" }} className="pe-1">
                    <Skeleton width="40%" height="2rem" />
                  </div>
                </div>

                <div className="d-flex ps-3 pe-3 mb-3">
                  <center style={{ width: "5%" }} className="pe-1">
                    <Skeleton width="2rem" height="2rem" shape="circle" />
                  </center>
                  <div style={{ width: "100%" }} className="pe-1">
                    <Skeleton width="35%" height="2rem" />
                  </div>
                </div>

                <div className="d-flex ps-3 pe-3 mb-3">
                  <center style={{ width: "5%" }} className="pe-1">
                    <Skeleton width="2rem" height="2rem" shape="circle" />
                  </center>
                  <div style={{ width: "100%" }} className="pe-1">
                    <Skeleton width="30%" height="2rem" />
                  </div>
                </div>

                <div className="d-flex ps-3 pe-3 mb-3">
                  <center style={{ width: "5%" }} className="pe-1">
                    <Skeleton width="2rem" height="2rem" shape="circle" />
                  </center>
                  <div style={{ width: "100%" }} className="pe-1">
                    <Skeleton width="45%" height="2rem" />
                  </div>
                </div>

                <div className="d-flex ps-3 pe-3 mb-3">
                  <center style={{ width: "5%" }} className="pe-1">
                    <Skeleton width="2rem" height="2rem" shape="circle" />
                  </center>
                  <div style={{ width: "100%" }} className="pe-1">
                    <Skeleton width="40%" height="2rem" />
                  </div>
                </div>
                <div className="d-flex justify-content-between mb-4 ps-3 pe-3">
                  <Skeleton width="100%" height="2px" />
                </div>

                <div className="d-flex justify-content-between ps-3 pe-3 mb-3">
                  <Skeleton width="12rem" height="2rem" />
                  <Skeleton width="12rem" height="2rem" />
                </div>
              </div>
            ) : (
              <ReviewMCQExamQuestion
                index={selectedIndex}
                input={selectedCard}
              ></ReviewMCQExamQuestion>
            )}
            {!loading && questions && questions.length > 0 && (
              <div className="d-flex pt-3 ps-2">
                <div>
                  {selectedIndex > 0 && (
                    <button
                      className="p-0 btn btn-outline"
                      onClick={moveToPrevious}
                    >
                      <div className="d-flex">
                        <img
                          src={isDark ? WhitePreviousIcon : PreviousIcon}
                          alt="Previous"
                          style={{ width: "40px", marginLeft: "-3px" }}
                        />
                        <div className="me-4 mt-2 ms-3">Previous</div>
                      </div>
                    </button>
                  )}
                </div>
                <div className="ms-auto pe-2">
                  {selectedIndex < questions.length - 1 && (
                    <button
                      className="p-0 btn btn-outline"
                      onClick={moveToNext}
                    >
                      <div className="d-flex m-auto">
                        <div className="me-4 mt-2 ms-3">Next</div>
                        <img
                          src={isDark ? WhiteNextIcon : NextIcon}
                          style={{ width: "40px", marginRight: "-3px" }}
                          alt="Previous"
                        />
                      </div>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ReviewQuizExam;
